import { FileQuestion, MessagesSquare } from "lucide-react";
import { pick } from "@/components/Translate";
import { t, locale } from "@/i18n";
import { Tt, useTitle } from "@/components/Tt";

export const groupKey = (q) => (q.citations?.length ? q.citations[0].sop_id : "unanswered");
export const isUnread = (q) => !!q.owner_reply && q.reply_seen === false;

const timeLabel = (iso) => {
  const d = new Date(iso);
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString(locale(), { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString(locale(), { month: "short", day: "numeric" });
};

export function buildGroups(thread) {
  const map = {};
  thread.filter((q) => !q.pending).forEach((q) => {
    const key = groupKey(q);
    const g = (map[key] ||= { key, title: key === "unanswered" ? t("Not in knowledge base") : q.citations[0].sop_title, items: [] });
    g.items.push(q);
  });
  return Object.values(map)
    .map((g) => ({ ...g, last: g.items[g.items.length - 1], unread: g.items.filter(isUnread).length }))
    .sort((a, b) => (b.last.created_at > a.last.created_at ? 1 : -1));
}

function Row({ active, onClick, icon, title, preview, time, unread, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${active ? "bg-[var(--accent-primary)] text-[var(--accent-ink)]" : "hover:bg-[var(--bg-surface-hover)]"}`}>
      <span className="w-2.5 shrink-0">{unread > 0 && <span className={`block w-2.5 h-2.5 rounded-full ${active ? "bg-white" : "bg-[var(--accent-primary)]"}`} data-testid={`${testid}-unread-dot`} />}</span>
      <span className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${active ? "bg-white/20" : "bg-[var(--bg-surface)]"}`}>{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="flex justify-between gap-2"><span className="font-semibold text-sm truncate">{title}</span><span className={`text-xs shrink-0 ${active ? "opacity-80" : "faint"}`}>{time}</span></span>
        <span className={`block text-xs truncate ${active ? "opacity-80" : "faint"}`}>{preview}</span>
      </span>
    </button>
  );
}

const Initial = ({ text }) => <span className="font-bold">{useTitle(text)[0]}</span>;

export function MessageList({ groups, selected, onSelect, total }) {
  return (
    <div className="stack-sm" data-testid="message-list">
      <Row active={selected === "all"} onClick={() => onSelect("all")} icon={<MessagesSquare size={18} />} title={t("All messages")}
        preview={t(total === 1 ? "{n} question" : "{n} questions", { n: total })} time="" unread={groups.reduce((n, g) => n + g.unread, 0)} testid="message-group-all" />
      {groups.map((g) => (
        <Row key={g.key} active={selected === g.key} onClick={() => onSelect(g.key)} testid={`message-group-${g.key}`}
          icon={g.key === "unanswered" ? <FileQuestion size={18} /> : <Initial text={g.title} />}
          title={g.key === "unanswered" ? g.title : <Tt text={g.title} />} preview={g.last.owner_reply ? `${t("Owner")}: ${pick(g.last, "owner_reply", false)}` : pick(g.last, "question", false)}
          time={timeLabel(g.last.created_at)} unread={g.unread} />
      ))}
    </div>
  );
}
